import React, { useEffect, useRef } from 'react'
import { Link, useLocation, useHistory } from 'react-router-dom'
import SiteFooter from '../../components/site-footer'

import useAuth0ClientConfig from '../../lib/auth0/use-auth0-client-config'
import useNotifications from '../../lib/notifications/use-notifications'
import isElementInViewport from '../../lib/isElementInViewport'
import TaskHeader from './task-header'
import style from './index.module.scss'
import useAnalytics from '../../lib/analytics/use-analytics'
import { trackBackButton } from '../../lib/analytics/events'
import { DEFAULT, SIGN_UP, LOGIN } from '../paths'
import Notification from '../../components/notification'
import useQueryContext from '../../lib/queryParams/use-query-context'

interface Props {
  children: React.ReactNode
}

interface LocationState {
  isInitialRedirect?: boolean
}

const Layout: React.FC<Props> = (props) => {
  const { pathname, state } = useLocation<LocationState>()
  const history = useHistory()
  const { dataLayer } = useAnalytics()
  const { isContextMigrate } = useQueryContext()
  const { consumerUrl } = useAuth0ClientConfig()

  const handleClickBack: () => void = () => {
    dataLayer(trackBackButton())

    if (isContextMigrate) {
      // A context of 'migrate' results from a redirect after logging into account.
      // Need to send user back home manually, or they'll be redirected here again.
      window.location.replace(`${consumerUrl}/`)
    } else {
      window.history.back()
    }
  }

  const displayBackButton = () => {
    if (
      [DEFAULT, SIGN_UP, LOGIN].some((path) =>
        history.location.pathname.includes(path)
      )
    ) {
      return { label: 'Back', handleClickBack }
    } else {
      return undefined
    }
  }

  const { notifications, clearNotifications, clearNotification } =
    useNotifications()

  const latestNotification = useRef<HTMLDivElement>(null)
  const isFirstRun = useRef(true)

  useEffect(() => {
    // don’t clear notifications for the first render
    if (isFirstRun.current || state?.isInitialRedirect) {
      isFirstRun.current = false
      return
    }

    clearNotifications()
  }, [pathname, clearNotifications, state])

  useEffect(() => {
    const el = latestNotification.current

    // don’t scroll to latest notification for the first render
    if (!el || isFirstRun.current || isElementInViewport(el)) {
      return
    }

    el.scrollIntoView({ behavior: 'smooth' })
  }, [notifications])

  return (
    <>
      <TaskHeader
        hairline
        Link={Link}
        logoType="default"
        back={displayBackButton()}
      />

      <div className={style.contentWrapper}>
        {notifications.length > 0 ? (
          <div className={style.notifications} data-testid="notifications">
            {notifications.map((notification) => (
              <div
                key={notification.id}
                ref={latestNotification}
                className={style.notification}
                data-testid="notification"
              >
                <Notification
                  type={notification.type}
                  message={notification.message}
                  onClickDismiss={() => clearNotification(notification.id)}
                />
              </div>
            ))}
          </div>
        ) : null}

        {props.children}
      </div>

      <SiteFooter
        colour="seasonal"
        links={[
          { label: 'Careers', href: 'https://www.scentregroup.com/careers' },
          {
            label: 'Business Solutions',
            href: 'https://www.scentregroup.com/business-solutions',
          },
          {
            label: 'Sustainability',
            href: 'https://www.scentregroup.com/sustainability',
          },
          {
            label: 'Privacy Policy',
            href: `${consumerUrl}/privacy-policy`,
          },
          {
            label: 'Terms & Conditions',
            href: `${consumerUrl}/terms-and-conditions`,
          },
        ]}
        logoUrl={consumerUrl}
      />
    </>
  )
}

export default Layout
